import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { jobTitleApi, F902 } from '../../../services/jobTitleApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useJobTitleForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F902[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobTitleApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f902_01);
      setName(current.f902_11);
      setContent(current.f902_12 || '');
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setName('');
    setContent('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f902_01);
    setName(current.f902_11);
    setContent(current.f902_12 || '');
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f902_01);
      setName(current.f902_11);
      setContent(current.f902_12 || '');
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await jobTitleApi.create({ f902_01: code, f902_11: name, f902_12: content });
        message.success('新增成功');
      } else {
        await jobTitleApi.update(current!.f902_01, { f902_11: name, f902_12: content });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f902_01;
      const idx = data.findIndex(r => r.f902_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await jobTitleApi.delete(current.f902_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f902_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    name, setName,
    content, setContent,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
